import { whenOf } from "../versions/Versions";
import type { IconName } from "../../components/ui/Icon";
import type { LibraryEntry } from "../../lib/libraryApi";

/**
 * The Library as the shelf shows it: one item per skill or tool, with every version (Flashadz's and the owner's,
 * oldest first), the one in use, and a newer Flashadz version waiting for the owner when they have their own.
 */

export type Who = "flashadz" | "you";

export interface Version {
  id: string;
  n: number;
  who: Who;
  at: string;
  note: string;
}

export interface ShelfItem {
  key: string;
  kind: string;
  name: string;
  title: string;
  /** Its short line: what it is for, in 8 words or fewer. */
  line: string;
  description: string;
  icon: IconName;
  versions: Version[];
  inUse: Version;
  /** The newer Flashadz version waiting for the owner, when they have their own. */
  waits: Version | null;
  /** The latest Flashadz version, which the owner's own words are compared with. */
  flashadz: Version | null;
}

/** Where each skill sits, by channel, and each tool, by the stage of a goal. Others go last. */
export const CHANNELS: { title: string; names: string[] }[] = [
  { title: "Every channel", names: ["plain-words", "measurement", "market-research"] },
  { title: "Meta", names: ["meta-ads", "ad-creative"] },
  { title: "Cold email", names: ["cold-email", "lead-research"] },
  { title: "Google", names: ["google-ads"] },
  { title: "LinkedIn", names: ["linkedin-ads"] },
];
export const STAGES: { title: string; when: string; names: string[] }[] = [
  { title: "Start a goal", when: "When you add a product or a goal", names: ["product-research", "playbook-draft", "goal-planning"] },
  { title: "Get ready", when: "After you approve the plan", names: ["ad-drafting", "email-drafting", "list-building"] },
  { title: "While it runs", when: "Every day, and when someone replies", names: ["daily-review", "goal-chat", "reply-draft"] },
  { title: "At the end", when: "When it reaches its date or its number", names: ["wrap-up"] },
];

const ICONS: Record<string, IconName> = {
  "plain-words": "doc",
  measurement: "target",
  "meta-ads": "bolt",
  "cold-email": "mail",
  "ad-creative": "film",
  "market-research": "search",
  "lead-research": "users",
  "linkedin-ads": "users",
  "google-ads": "search",
  "product-research": "globe",
  "playbook-draft": "spark",
  "goal-planning": "calendar",
  "ad-drafting": "pen",
  "email-drafting": "send",
  "list-building": "form",
  "daily-review": "clock",
  "goal-chat": "reply",
  "reply-draft": "mail",
  "wrap-up": "check",
};

/** Where an item sits on the shelf: its channel or its stage. */
export function placeOf(it: Pick<ShelfItem, "kind" | "name">): string {
  const list = it.kind === "skill" ? CHANNELS : STAGES;
  return list.find((g) => g.names.includes(it.name))?.title ?? "Other";
}

const firstSentence = (s: string) => (s.match(/^(.+?[.!?])(\s|$)/)?.[1] ?? s).trim();

/** Skills and tools from the list, each with its versions; a goal's or a product's own playbook is not here. */
export function shelfItems(entries: LibraryEntry[]): ShelfItem[] {
  const keys = [...new Set(entries.filter((e) => e.kind === "skill" || e.kind === "method").map((e) => `${e.kind}|${e.name}`))];
  return keys.map((key) => {
    const [kind, name] = key.split("|");
    const flash = entries.find((e) => e.origin === "flashadz" && e.kind === kind && e.name === name);
    const own = entries.find((e) => e.origin === "account" && e.kind === kind && e.name === name);
    const fv: Version[] = (flash?.versions ?? []).map((v) => ({ id: v.id, n: v.version, who: "flashadz", at: v.createdAt, note: v.note }));
    const ov: Version[] = (own?.versions ?? []).map((v) => ({ id: v.id, n: v.version, who: "you", at: v.createdAt, note: v.note }));
    const versions = [...fv, ...ov].toSorted((a, b) => a.at.localeCompare(b.at) || (a.who === b.who ? a.n - b.n : a.who === "flashadz" ? -1 : 1));
    const top = own ?? flash!;
    const inUse = (own ? ov : fv).toSorted((a, b) => b.n - a.n)[0];
    const waits = own?.newerDefault ? (fv.find((v) => v.id === own.newerDefault!.id) ?? null) : null;
    const latestF = fv.toSorted((a, b) => b.n - a.n)[0] ?? null;
    return {
      key,
      kind,
      name,
      title: top.latest.title,
      line: top.latest.tagline || flash?.latest.tagline || firstSentence(top.latest.description),
      description: top.latest.description,
      icon: ICONS[name] ?? (kind === "skill" ? "tool" : "spark"),
      versions,
      inUse,
      waits,
      flashadz: latestF,
    };
  });
}

export const labelOf = (v: Version) => (v.who === "you" ? `Yours v${v.n}` : `Flashadz v${v.n}`);

/** The version a version came from: the one before it of the same kind, or for the owner's first, the Flashadz one in use then. */
export function previousOf(it: ShelfItem, v: Version): Version | null {
  const same = it.versions.filter((x) => x.who === v.who && x.n < v.n).toSorted((a, b) => b.n - a.n)[0];
  if (same) return same;
  if (v.who === "you") return it.versions.filter((x) => x.who === "flashadz" && x.at <= v.at).toSorted((a, b) => b.n - a.n)[0] ?? null;
  return null;
}

/** The day only: "8 Oct". */
export const dayOf = (at: string) => whenOf(at).split(",")[0];

/** Items that match the search, by name, short line or what they are for. */
export function matches(it: ShelfItem, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [it.title, it.line, it.description, it.name.replace(/-/g, " ")].join(" ").toLowerCase().includes(s);
}
